/**
 * Data extraction functions for safety chart generators.
 * Each function reads raw Modbus register data, extracts config bits,
 * and calls the corresponding pure chart generator.
 *
 * Separated from section-registry.ts for clarity and to contain
 * the bitwise operations in a dedicated file.
 */
/* eslint-disable no-bitwise */
import { getStyleProp } from 'ngx-core-services';

import {
  SafetyChartOutput,
  ProtectionPoint,
  generateProtectionChart,
  generateFullPUDerateChart,
  generateFreqDerateChart,
  generateCosPhiPChart,
  generateQUGradientChart,
  generateQUStepChart,
  generateVRTChart,
} from './safety-chart-generators';

type ChartGeneratorFn = (data: Record<string, any>) => SafetyChartOutput | SafetyChartOutput[] | null;

/** Helper: extract a number from data, return null if not finite */
const num = (value: unknown, fallback = 0): number => {
  const n = Number(value ?? fallback);
  return isFinite(n) ? n : fallback;
};

/** Helper: extract config register bits, return 0 if not finite */
const configBits = (value: unknown): number => {
  const n = Number(value ?? 0);
  return isFinite(n) ? n : 0;
};

/** Helper: check if a specific bit is set in a config register */
const hasBit = (config: number, bit: number): boolean => !!(config & (1 << bit));

/** Helper: extract N bits starting at position */
const extractBits = (config: number, start: number, count: number): number =>
  (config >> start) & ((1 << count) - 1);

// ── Extractors ──────────────────────────────────────────────

/** Extract protection levels (OV/UV, OF/UF, DCI) from config bits + register data */
const extractProtectionLevels = (
  data: Record<string, any>,
  config: number,
  levels: Array<{ bit: number; valKey: string; timeKey: string; label: string; color: string }>,
): ProtectionPoint[] => {
  const points: ProtectionPoint[] = [];
  for (const { bit, valKey, timeKey, label, color } of levels) {
    if (hasBit(config, bit)) {
      const v = num(data[valKey]);
      const t = num(data[timeKey]);
      if (v && t) { points.push({ label, value: v, time: t, color }); }
    }
  }
  return points;
};

export const extractVoltageProtectionChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.VoltageConfig);
  const rated = num(data.RatedVoltage);
  if (!rated) { return null; }

  const points = extractProtectionLevels(data, config, [
    { bit: 0, valKey: 'FirstOvervoltageProtectionValue', timeKey: 'FirstOvervoltageProtectionTime', label: 'OV1', color: getStyleProp('--orange-300') },
    { bit: 1, valKey: 'SecondOvervoltageProtectionValue', timeKey: 'SecondOvervoltageProtectionTime', label: 'OV2', color: getStyleProp('--red-300') },
    { bit: 2, valKey: 'ThirdOvervoltageProtectionValue', timeKey: 'ThirdOvervoltageProtectionTime', label: 'OV3', color: getStyleProp('--red-500') },
    { bit: 3, valKey: 'FirstUndervoltageProtectionValue', timeKey: 'FirstUndervoltageProtectionTime', label: 'UV1', color: getStyleProp('--orange-300') },
    { bit: 4, valKey: 'SecondUndervoltageProtectionValue', timeKey: 'SecondUndervoltageProtectionTime', label: 'UV2', color: getStyleProp('--red-300') },
    { bit: 5, valKey: 'ThirdUndervoltageProtectionValue', timeKey: 'ThirdUndervoltageProtectionTime', label: 'UV3', color: getStyleProp('--red-500') },
  ]);

  return generateProtectionChart(points, { value: rated, label: 'Rated Voltage' }, { yAxisLabel: 'V', xAxisLabel: 'ms' });
};

export const extractFrequencyProtectionChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.FrequencyConfig);
  const rated = num(data.RatedFrequency);
  if (!rated) { return null; }

  const points = extractProtectionLevels(data, config, [
    { bit: 0, valKey: 'FirstOverfrequencyProtectionValue', timeKey: 'FirstOverfrequencyProtectionTime', label: 'OF1', color: getStyleProp('--orange-300') },
    { bit: 1, valKey: 'SecondOverfrequencyProtectionValue', timeKey: 'SecondOverfrequencyProtectionTime', label: 'OF2', color: getStyleProp('--red-300') },
    { bit: 2, valKey: 'ThirdOverfrequencyProtectionValue', timeKey: 'ThirdOverfrequencyProtectionTime', label: 'OF3', color: getStyleProp('--red-500') },
    { bit: 3, valKey: 'FirstUnderfrequencyProtectionValue', timeKey: 'FirstUnderfrequencyProtectionTime', label: 'UF1', color: getStyleProp('--orange-300') },
    { bit: 4, valKey: 'SecondUnderfrequencyProtectionValue', timeKey: 'SecondUnderfrequencyProtectionTime', label: 'UF2', color: getStyleProp('--red-300') },
    { bit: 5, valKey: 'ThirdUnderfrequencyProtectionValue', timeKey: 'ThirdUnderfrequencyProtectionTime', label: 'UF3', color: getStyleProp('--red-500') },
  ]);

  return generateProtectionChart(points, { value: rated, label: 'Rated Frequency' }, { yAxisLabel: 'Hz', xAxisLabel: 'ms' });
};

export const extractDCIProtectionChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.DCIConfig);

  const points = extractProtectionLevels(data, config, [
    { bit: 0, valKey: 'DCIFirstProtectionValue', timeKey: 'DCIFirstProtectionTime', label: 'DCI L1', color: getStyleProp('--orange-300') },
    { bit: 1, valKey: 'DCISecondProtectionValue', timeKey: 'DCISecondProtectionTime', label: 'DCI L2', color: getStyleProp('--red-300') },
    { bit: 2, valKey: 'DCIThirdProtectionValue', timeKey: 'DCIThirdProtectionTime', label: 'DCI L3', color: getStyleProp('--red-500') },
  ]);

  if (!points.length) { return null; }
  return generateProtectionChart(points, undefined, { yAxisLabel: 'mA', xAxisLabel: 'ms' });
};

export const extractActiveDerateChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.RemoteConfig);
  const hasOV = hasBit(config, 2);
  const hasUV = hasBit(config, 4);
  if (!hasOV && !hasUV) { return null; }

  const overVoltage = hasOV ? {
    start: num(data.GridVoltageDropStart),
    stop: num(data.GridVoltageDropStop),
    minPower: num(data.GridVoltageDropMinPower),
  } : null;

  const underVoltage = hasUV ? {
    start: num(data.ChgDerateVoltStart),
    stop: num(data.ChgDerateVoltEnd),
    minPower: num(data.ChgDerateMinPower),
  } : null;

  return generateFullPUDerateChart(overVoltage, underVoltage, { title: 'P(U) Derate' });
};

export const extractFreqDerateChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.FrequencyDerateConfig);
  const hasOF = hasBit(config, 0);
  const hasUF = hasBit(config, 1);
  if (!hasOF && !hasUF) { return null; }

  const mode: 'slope' | 'range' = extractBits(config, 12, 2) === 1 ? 'range' : 'slope';

  const overFreq = hasOF ? {
    startFreq: num(data.OverfrequencyStart),
    endFreq: num(data.OverfrequencyEnd),
    slope: num(data.OverfrequencySlope),
    ratedFreq: 50,
  } : null;

  const underFreq = hasUF ? {
    startFreq: num(data.UnderfrequencyStart),
    endFreq: num(data.UnderfrequencyEnd),
    slope: num(data.UnderfrequencySlope),
    ratedFreq: 50,
  } : null;

  return generateFreqDerateChart(overFreq, underFreq, mode);
};

export const extractReactivePowerChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.ReactiveConfig);
  if (!hasBit(config, 0)) { return null; }

  const mode = extractBits(config, 1, 3);

  if (mode === 2) {
    return generateCosPhiPChart([
      { power: num(data.ReactivedynamicValue1), cosPhi: num(data.ReactiveCos1) },
      { power: num(data.ReactivedynamicValue2), cosPhi: num(data.ReactiveCos2) },
      { power: num(data.ReactivedynamicValue3), cosPhi: num(data.ReactiveCos3) },
      { power: num(data.ReactivedynamicValue4), cosPhi: num(data.ReactiveCos4) },
    ]);
  }

  if (mode === 3) {
    return generateQUGradientChart({
      u1: num(data.LowVoltEndValue4),
      u2: num(data.LowVoltStartValue4),
      u3: num(data.HighVoltStartValue4),
      u4: num(data.HighVoltEndValue4),
      qMax: num(data.MaxLaggingReactivePower4),
      qMin: -(num(data.MaxLeadingReactivePower4)),
      qStart: num(data.ReactivePowerStart4),
      offset: num(data.ReactivePowerOffset4),
    });
  }

  if (mode === 4) {
    return generateQUStepChart({
      u1Rising: num(data.LowVoltEndValue5),
      u2Rising: num(data.LowVoltStartValue5),
      u3Falling: num(data.HighVoltStartValue5),
      u4Falling: num(data.HighVoltEndValue5),
      qMax: num(data.MaxReactivePower5),
    });
  }

  return null;
};

export const extractVRTChart: ChartGeneratorFn = (data) => {
  const config = configBits(data.VRTConfig);
  const hasLVRT = hasBit(config, 0);
  const hasOVRT = hasBit(config, 1);
  if (!hasLVRT && !hasOVRT) { return null; }

  const charts: SafetyChartOutput[] = [];

  if (hasLVRT) {
    const result = generateVRTChart({
      entryVoltage: num(data.LvrtInVolt),
      exitVoltage: num(data.LvrtOutVolt),
      type: 'LVRT',
      points: [
        { voltage: num(data.LvrtFirstPointVolt), time: num(data.LvrtFirstPointTime) },
        { voltage: num(data.LvrtSecondPointVolt), time: num(data.LvrtSecondPointTime) },
        { voltage: num(data.LvrtThirdPointVolt), time: num(data.LvrtThirdPointTime) },
        { voltage: num(data.LvrtFourthPointVolt), time: num(data.LvrtFourthPointTime) },
      ],
    });
    if (result) { charts.push(result); }
  }

  if (hasOVRT) {
    const result = generateVRTChart({
      entryVoltage: num(data.OvrtInVlot),
      exitVoltage: num(data.OvrtOutVolt),
      type: 'OVRT',
      points: [
        { voltage: num(data.OvrtFirstPointVolt), time: num(data.OvrtFirstPointTime) },
        { voltage: num(data.OvrtSecondPointVolt), time: num(data.OvrtSecondPointTime) },
        { voltage: num(data.OvrtThirdPointVolt), time: num(data.OvrtThirdPointTime) },
        { voltage: num(data.OvrtFourthPointVolt), time: num(data.OvrtFourthPointTime) },
      ],
    });
    if (result) { charts.push(result); }
  }

  return charts.length ? charts : null;
};
